import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
  Settings, 
  Save, 
  RefreshCw,
  ToggleLeft,
  ToggleRight,
  Bus,
  Route,
  Table2,
  Wrench,
  Fuel,
  FileText,
  Users,
  Calendar,
  BarChart3,
  Bell,
  Mail,
  MessageSquare,
  Shield,
  ClipboardList,
  Euro,
  History,
  Cog,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileSpreadsheet
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Module icons mapping
const moduleIcons = {
  trips: Route,
  trip_planner: Calendar,
  trip_import: FileSpreadsheet,
  excel_view: Table2,
  mechanic: Wrench,
  refueling: Fuel,
  reports: FileText,
  employee_reports: Users,
  bus_calendar: Calendar,
  bus_analytics: BarChart3,
  notifications_telegram: MessageSquare,
  notifications_email: Mail,
  user_management: Users,
  bus_management: Bus,
  bus_parameters: Cog,
  driver_salary: Euro,
  audit_log: History,
  project_tasks: ClipboardList,
};

// Module categories
const moduleCategories = {
  core: {
    name: 'Основные модули',
    description: 'Ключевые функции системы',
    modules: ['trips', 'trip_planner', 'trip_import', 'excel_view', 'bus_management', 'bus_parameters']
  },
  staff: {
    name: 'Персонал',
    description: 'Управление сотрудниками и расчёты',
    modules: ['user_management', 'mechanic', 'driver_salary', 'refueling']
  },
  reports: {
    name: 'Отчёты и аналитика',
    description: 'Отчёты, статистика и аналитика',
    modules: ['reports', 'employee_reports', 'bus_calendar', 'bus_analytics']
  },
  notifications: {
    name: 'Уведомления',
    description: 'Каналы уведомлений',
    modules: ['notifications_telegram', 'notifications_email']
  },
  system: {
    name: 'Система',
    description: 'Системные функции',
    modules: ['audit_log', 'project_tasks']
  }
};

export default function SystemModules() {
  const [modules, setModules] = useState({});
  const [originalModules, setOriginalModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/api/system/modules`, getAuthHeaders());
      setModules(response.data.modules || {});
      setOriginalModules(response.data.modules || {});
      setHasChanges(false);
    } catch (error) {
      toast.error('Ошибка загрузки модулей');
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleKey) => {
    setModules(prev => {
      const updated = {
        ...prev,
        [moduleKey]: {
          ...prev[moduleKey],
          enabled: !prev[moduleKey]?.enabled
        }
      };
      
      // Check if there are changes
      const hasAnyChanges = Object.keys(updated).some(key => 
        updated[key]?.enabled !== originalModules[key]?.enabled
      );
      setHasChanges(hasAnyChanges);
      
      return updated;
    });
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/system/modules`, { modules }, getAuthHeaders());
      setOriginalModules(modules);
      setHasChanges(false);
      toast.success('Настройки модулей сохранены');
      
      // Reload page to apply changes
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error('Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const resetChanges = () => {
    setModules(originalModules);
    setHasChanges(false);
  };

  const enableAll = () => {
    const allEnabled = {};
    Object.keys(modules).forEach(key => {
      allEnabled[key] = { ...modules[key], enabled: true };
    });
    setModules(allEnabled);
    setHasChanges(true);
  };

  const disableAll = () => {
    const allDisabled = {};
    Object.keys(modules).forEach(key => {
      allDisabled[key] = { ...modules[key], enabled: false };
    });
    setModules(allDisabled);
    setHasChanges(true);
  };

  // Count enabled/disabled modules
  const enabledCount = Object.values(modules).filter(m => m?.enabled !== false).length;
  const totalCount = Object.keys(modules).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Settings className="w-6 h-6 text-purple-600" />
            Управление модулями
          </h2>
          <p className="text-gray-500 mt-1">
            Включайте и выключайте функции системы для вашей организации
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {enabledCount} из {totalCount} включено
          </span>
        </div>
      </div>

      {/* Warning banner */}
      {hasChanges && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-amber-800 font-medium">Есть несохранённые изменения</p>
            <p className="text-amber-600 text-sm">Нажмите "Сохранить" чтобы применить изменения. Страница перезагрузится.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={resetChanges}>
              Отмена
            </Button>
            <Button size="sm" onClick={saveChanges} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={enableAll}>
          <CheckCircle className="w-4 h-4 mr-1 text-green-600" />
          Включить все
        </Button>
        <Button variant="outline" size="sm" onClick={disableAll}>
          <XCircle className="w-4 h-4 mr-1 text-red-600" />
          Выключить все
        </Button>
        <Button variant="outline" size="sm" onClick={fetchModules}>
          <RefreshCw className="w-4 h-4 mr-1" />
          Обновить
        </Button>
      </div>

      {/* Module Categories */}
      <div className="space-y-6">
        {Object.entries(moduleCategories).map(([categoryKey, category]) => (
          <Card key={categoryKey} className="overflow-hidden">
            <CardHeader className="bg-gray-50 py-4">
              <CardTitle className="text-lg">{category.name}</CardTitle>
              <CardDescription>{category.description}</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {category.modules.map(moduleKey => {
                  const module = modules[moduleKey];
                  if (!module) return null;
                  
                  const Icon = moduleIcons[moduleKey] || Settings;
                  const isEnabled = module.enabled !== false;
                  
                  return (
                    <div 
                      key={moduleKey}
                      className={`flex items-center justify-between p-4 transition-colors ${
                        isEnabled ? 'bg-white' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          isEnabled ? 'bg-purple-100 text-purple-600' : 'bg-gray-200 text-gray-400'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className={`font-medium ${isEnabled ? 'text-gray-800' : 'text-gray-400'}`}>
                            {module.name}
                          </h4>
                          <p className={`text-sm ${isEnabled ? 'text-gray-500' : 'text-gray-400'}`}>
                            {module.description}
                          </p>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => toggleModule(moduleKey)}
                        className="focus:outline-none"
                      >
                        {isEnabled ? (
                          <ToggleRight className="w-10 h-10 text-green-500 hover:text-green-600 transition-colors" />
                        ) : (
                          <ToggleLeft className="w-10 h-10 text-gray-300 hover:text-gray-400 transition-colors" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Save button (fixed at bottom on mobile) */}
      {hasChanges && (
        <div className="fixed bottom-4 left-4 right-4 sm:static sm:flex sm:justify-end">
          <Button 
            onClick={saveChanges} 
            disabled={saving}
            className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 shadow-lg"
            size="lg"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Сохранение...' : 'Сохранить изменения'}
          </Button>
        </div>
      )}
    </div>
  );
}
